import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, ArrowRight, Calendar, Thermometer, Waves, MapPin, Compass,
  Sparkles, Star, Anchor,
} from "lucide-react";
import { useI18n } from "@/i18n";

const MONTH_KEYS = ["april", "may", "june", "july", "august", "september", "october"];
const MONTH_LABELS = {
  it: ["Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre"],
  en: ["April", "May", "June", "July", "August", "September", "October"],
};
const RATING_META = {
  exceptional: { it: "Eccellente", en: "Exceptional", bg: "#C4A26C", text: "#0B1A2D" },
  excellent: { it: "Ottimo", en: "Excellent", bg: "#0B1A2D", text: "#FFFFFF" },
  good:      { it: "Buono",  en: "Good",      bg: "#10B981", text: "#FFFFFF" },
  possible:  { it: "Possibile", en: "Possible", bg: "#F59E0B", text: "#FFFFFF" },
  off:       { it: "Fuori stagione", en: "Off-season", bg: "#E2E8F0", text: "#64748B" },
};

function Score({ value }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-3.5 w-3.5 ${i <= value ? "text-gold fill-gold" : "text-ink/20"}`} />
      ))}
    </span>
  );
}

export default function DestinationDetail() {
  const { slug } = useParams();
  const { lang, localizePath } = useI18n();
  const navigate = useNavigate();
  const EN = lang === "en";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setNotFound(false);
    api.get(`/destinations/public/${slug}?lang=${lang}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch((err) => { if (!cancel && err?.response?.status === 404) setNotFound(true); })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [slug, lang]);

  if (loading) {
    return <div className="min-h-screen pt-32 text-center text-ink/60">{EN ? "Loading…" : "Caricamento…"}</div>;
  }

  if (notFound || !data) {
    return (
      <div data-testid="dest-not-found" className="min-h-screen pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <h1 className="font-serif text-3xl text-navy">{EN ? "Destination not found" : "Destinazione non trovata"}</h1>
          <Button onClick={() => navigate(localizePath("/destinazioni"))} className="mt-6 bg-navy text-white hover:bg-[#06101f] rounded-full h-11 px-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> {EN ? "All destinations" : "Tutte le destinazioni"}
          </Button>
        </div>
      </div>
    );
  }

  const destinationsRoot = EN ? "/destinations" : "/destinazioni";
  return (
    <div data-testid="destination-detail-page" className="bg-white">
      {/* HERO IMAGE */}
      <section className="relative">
        <div className="relative h-[60vh] min-h-[420px] w-full overflow-hidden bg-navy">
          <img src={data.hero_image} alt={data.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/30 to-navy/70" />
          <div className="absolute inset-0 flex flex-col justify-end">
            <div className="max-w-5xl mx-auto px-5 lg:px-10 pb-12 w-full text-white">
              <Link to={localizePath(destinationsRoot)} className="inline-flex items-center text-sm text-white/80 hover:text-white">
                <ArrowLeft className="h-4 w-4 mr-1" /> {EN ? "All destinations" : "Tutte le destinazioni"}
              </Link>
              <p className="mt-6 text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">{data.type_label}</p>
              <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">{data.name}</h1>
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80">
                <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-gold" /> {data.country}</span>
                <span className="inline-flex items-center gap-1.5"><Thermometer className="h-4 w-4 text-gold" /> {data.climate?.air_min}–{data.climate?.air_max}°C {EN ? "air" : "aria"}</span>
                <span className="inline-flex items-center gap-1.5"><Waves className="h-4 w-4 text-gold" /> {data.climate?.sea_min}–{data.climate?.sea_max}°C {EN ? "sea" : "mare"}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SUMMARY */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <p className="label-eyebrow">{EN ? "Overview" : "Panoramica"}</p>
          <p className="mt-4 text-lg text-ink leading-relaxed">{data.summary}</p>
        </div>
      </section>

      {/* WHY CHOOSE IT */}
      {data.why_choose?.length > 0 && (
        <section className="py-12 bg-sand">
          <div className="max-w-5xl mx-auto px-5 lg:px-10">
            <p className="label-eyebrow">{EN ? "Why choose it" : "Perché sceglierla"}</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-4">
              {data.why_choose.map((r, i) => (
                <li key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-[#E5E1D2]">
                  <span className="h-7 w-7 rounded-full bg-navy text-white inline-flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                  <span className="text-sm text-ink leading-relaxed">{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* BEST PERIOD */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-5 lg:px-10">
          <p className="label-eyebrow">{EN ? "Ideal period" : "Periodo ideale"}</p>
          <div className="mt-6 grid grid-cols-7 gap-2 text-center text-xs">
            {MONTH_KEYS.map((k, i) => {
              const rating = data.best_period?.[k] || "off";
              const meta = RATING_META[rating];
              return (
                <div key={k} className="rounded-lg overflow-hidden border border-[#E2E8F0]">
                  <div className="bg-white py-2 font-semibold text-navy">{MONTH_LABELS[lang][i].slice(0, 3)}</div>
                  <div className="py-2 text-[10px] uppercase tracking-wider font-semibold" style={{ background: meta.bg, color: meta.text }}>
                    {meta[lang]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ACTIVITIES */}
      {data.activities?.length > 0 && (
        <section className="py-12 bg-sand">
          <div className="max-w-5xl mx-auto px-5 lg:px-10">
            <p className="label-eyebrow">{EN ? "Popular experiences" : "Esperienze più richieste"}</p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {data.activities.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-[#E5E1D2] rounded-full px-4 py-2 text-sm text-navy">
                  <Compass className="h-3.5 w-3.5 text-gold" /> {a}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ITINERARY */}
      {data.itinerary && (
        <section className="py-12">
          <div className="max-w-3xl mx-auto px-5 lg:px-10">
            <p className="label-eyebrow">{EN ? "Suggested itinerary" : "Itinerario tipico in barca"}</p>
            <div className="mt-6 p-6 rounded-2xl bg-navy text-white">
              <Anchor className="h-5 w-5 text-gold" />
              <p className="mt-3 font-serif text-lg leading-relaxed">{data.itinerary}</p>
            </div>
          </div>
        </section>
      )}

      {/* AUDIENCE */}
      {data.audience?.length > 0 && (
        <section className="py-12 bg-sand">
          <div className="max-w-3xl mx-auto px-5 lg:px-10">
            <p className="label-eyebrow">{EN ? "Recommended for" : "A chi la consigliamo"}</p>
            <ul className="mt-6 space-y-2.5">
              {data.audience.map((a, i) => (
                <li key={i} className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-[#E5E1D2]">
                  <span className="text-sm text-navy font-medium">{a.label}</span>
                  <Score value={a.score} />
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* BUDGET */}
      {data.budget?.length > 0 && (
        <section className="py-12">
          <div className="max-w-5xl mx-auto px-5 lg:px-10">
            <p className="label-eyebrow">{EN ? "Indicative budget" : "Budget indicativo"}</p>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              {data.budget.map((b, i) => (
                <div key={i} className="rounded-2xl bg-white border border-[#E2E8F0] p-5">
                  <p className="text-[10px] uppercase tracking-wider text-gold font-semibold">{b.label}</p>
                  <p className="mt-2 font-serif text-2xl text-navy">{b.range}</p>
                  <p className="mt-1 text-xs text-ink/60">{b.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CULTURE */}
      {data.culture && (
        <section className="py-12 bg-sand">
          <div className="max-w-3xl mx-auto px-5 lg:px-10">
            <p className="label-eyebrow">{EN ? "Cultural notes" : "Curiosità culturali"}</p>
            <div className="mt-6 p-6 rounded-2xl bg-white border border-[#E5E1D2]">
              <p className="text-base text-ink leading-relaxed">{data.culture}</p>
            </div>
          </div>
        </section>
      )}

      {/* CONVERSION CTA — premium concierge style */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <div className="rounded-2xl bg-gradient-to-br from-sand to-white border-2 border-gold/40 p-8 text-center">
            <p className="label-eyebrow text-gold">{EN ? `Thinking about ${data.name}?` : `Vuoi visitare ${data.name}?`}</p>
            <h3 className="mt-4 font-serif text-2xl sm:text-3xl text-navy leading-tight">
              {EN ? "Let Seamatch design the perfect itinerary for you." : "Pensa a tutto Seamatch."}
            </h3>
            <p className="mt-3 text-sm text-ink/70 max-w-md mx-auto">
              {EN
                ? "Share a short brief — your concierge will return with a tailored proposal within 24 hours."
                : "Raccontaci cosa cerchi: in 24 ore costruiamo una proposta su misura, con preventivi reali e alternative intelligenti."}
            </p>
            <Button
              data-testid="dest-detail-conversion-cta"
              onClick={() => navigate(localizePath("/search"))}
              className="mt-6 bg-navy text-white hover:bg-[#06101f] rounded-full h-12 px-8 text-sm font-semibold"
            >
              {EN ? "Get a tailored proposal" : "Ricevi una proposta"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 bg-navy text-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 text-center">
          <Sparkles className="h-6 w-6 mx-auto text-gold" />
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight leading-tight">
            {EN ? `Ready to sail ${data.name}?` : `Pronto a navigare a ${data.name}?`}
          </h2>
          <p className="mt-4 text-white/80">
            {EN
              ? "Share a short brief — your concierge will design a hand-built proposal within 24 hours."
              : "Raccontaci cosa cerchi. Entro 24 ore costruiamo una proposta su misura per te."}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              data-testid="dest-detail-cta-search"
              onClick={() => navigate(localizePath("/search"))}
              className="bg-gold text-navy hover:bg-[#c89e29] rounded-full h-12 px-7 text-sm font-semibold"
            >
              {EN ? "Brief my concierge" : "Inizia la richiesta"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button
              onClick={() => navigate(localizePath(destinationsRoot))}
              variant="outline"
              className="rounded-full h-12 px-7 text-sm font-semibold border-white/40 text-white hover:bg-white/10 hover:text-white"
            >
              {EN ? "Explore other destinations" : "Esplora altre destinazioni"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
